/**
 * Data-driven node type configuration for DAG visualization.
 *
 * Maps all 12 DAG NodeTypes to visual properties (icon, shape, color).
 * NO switch statements — lookup by enum key.
 *
 * Local enums match backend DAGNodeTypeEnum (12 types). These are defined locally
 * because workflow-forms is a library package — it should not import from the
 * portal app's generated types. Values match generated/graphql.ts DagnodeTypeEnum.
 */

import {
  IconCheckbox,
  IconGitBranch,
  IconFilter,
  IconUser,
  IconPlug,
  IconRobot,
  IconHierarchy,
  IconShieldCheck,
  IconScale,
  IconArrowsShuffle,
  IconSend,
  IconCamera,
} from '@tabler/icons-react';

// ── Icon type for config map ────────────────────────────────────────
// Tabler icons are ForwardRefExoticComponents; we only need size prop.
type TablerIconComponent = React.ComponentType<{ size?: number | string }>;

// ── Local DAG enums (matches backend DAGNodeTypeEnum, 12 types) ──────
// Library-local definitions matching generated/graphql.ts DagnodeTypeEnum.

export const DagNodeType = {
  task: 'task',
  decision: 'decision',
  condition: 'condition',
  human: 'human',
  integration: 'integration',
  autonomous: 'autonomous',
  subWorkflow: 'subWorkflow',
  validation: 'validation',
  policyEvaluation: 'policyEvaluation',
  sagaActivation: 'sagaActivation',
  actorDispatch: 'actorDispatch',
  factSnapshot: 'factSnapshot',
} as const;
export type DagNodeType = (typeof DagNodeType)[keyof typeof DagNodeType];

export const DagCriticality = {
  lifeCritical: 'lifeCritical',
  patientImpact: 'patientImpact',
  complianceCritical: 'complianceCritical',
  financiallySignificant: 'financiallySignificant',
  operational: 'operational',
} as const;
export type DagCriticality = (typeof DagCriticality)[keyof typeof DagCriticality];

export const DagNodeStatus = {
  pending: 'pending',
  running: 'running',
  completed: 'completed',
  failed: 'failed',
  skipped: 'skipped',
  requiresHuman: 'requiresHuman',
  paused: 'paused',
} as const;
export type DagNodeStatus = (typeof DagNodeStatus)[keyof typeof DagNodeStatus];

// ── Shape types for SVG rendering ────────────────────────────────────

export type NodeShape =
  | 'rect'
  | 'diamond'
  | 'hexagon'
  | 'rounded'
  | 'octagon'
  | 'parallelogram';

// ── Node type visual config ──────────────────────────────────────────

export interface NodeTypeVisualConfig {
  icon: TablerIconComponent;
  shape: NodeShape;
  color: string; // Mantine color token
  label: string;
  category: 'execution' | 'logic' | 'integration' | 'safety' | 'actor';
}

export const NODE_TYPE_CONFIG: Record<DagNodeType, NodeTypeVisualConfig> = {
  [DagNodeType.task]: {
    icon: IconCheckbox,
    shape: 'rect',
    color: 'blue',
    label: 'Task',
    category: 'execution',
  },
  [DagNodeType.decision]: {
    icon: IconGitBranch,
    shape: 'diamond',
    color: 'violet',
    label: 'Decision',
    category: 'logic',
  },
  [DagNodeType.condition]: {
    icon: IconFilter,
    shape: 'hexagon',
    color: 'orange',
    label: 'Condition',
    category: 'logic',
  },
  [DagNodeType.human]: {
    icon: IconUser,
    shape: 'rounded',
    color: 'teal',
    label: 'Human',
    category: 'execution',
  },
  [DagNodeType.integration]: {
    icon: IconPlug,
    shape: 'rect',
    color: 'cyan',
    label: 'Integration',
    category: 'integration',
  },
  [DagNodeType.autonomous]: {
    icon: IconRobot,
    shape: 'rounded',
    color: 'grape',
    label: 'Autonomous',
    category: 'execution',
  },
  [DagNodeType.subWorkflow]: {
    icon: IconHierarchy,
    shape: 'rect',
    color: 'indigo',
    label: 'Sub-Workflow',
    category: 'integration',
  },
  [DagNodeType.validation]: {
    icon: IconShieldCheck,
    shape: 'hexagon',
    color: 'green',
    label: 'Validation',
    category: 'safety',
  },
  [DagNodeType.policyEvaluation]: {
    icon: IconScale,
    shape: 'octagon',
    color: 'red',
    label: 'Policy Eval',
    category: 'safety',
  },
  [DagNodeType.sagaActivation]: {
    icon: IconArrowsShuffle,
    shape: 'rect',
    color: 'pink',
    label: 'Saga',
    category: 'integration',
  },
  [DagNodeType.actorDispatch]: {
    icon: IconSend,
    shape: 'rounded',
    color: 'lime',
    label: 'Actor Dispatch',
    category: 'actor',
  },
  [DagNodeType.factSnapshot]: {
    icon: IconCamera,
    shape: 'rect',
    color: 'gray',
    label: 'Fact Snapshot',
    category: 'safety',
  },
};

// ── Criticality visual config ────────────────────────────────────────

export interface CriticalityVisualConfig {
  borderColor: string;
  glowColor: string;
  pulseAnimation: boolean;
  label: string;
}

export const CRITICALITY_CONFIG: Record<DagCriticality, CriticalityVisualConfig> = {
  [DagCriticality.lifeCritical]: {
    borderColor: '#e03131',
    glowColor: 'rgba(224, 49, 49, 0.4)',
    pulseAnimation: true,
    label: 'Life-Critical',
  },
  [DagCriticality.patientImpact]: {
    borderColor: '#e8590c',
    glowColor: 'rgba(232, 89, 12, 0.3)',
    pulseAnimation: true,
    label: 'Patient Impact',
  },
  [DagCriticality.complianceCritical]: {
    borderColor: '#f08c00',
    glowColor: 'rgba(240, 140, 0, 0.25)',
    pulseAnimation: false,
    label: 'Compliance',
  },
  [DagCriticality.financiallySignificant]: {
    borderColor: '#1971c2',
    glowColor: 'rgba(25, 113, 194, 0.2)',
    pulseAnimation: false,
    label: 'Financial',
  },
  [DagCriticality.operational]: {
    borderColor: '#868e96',
    glowColor: 'transparent',
    pulseAnimation: false,
    label: 'Operational',
  },
};

// ── Node status visual config ────────────────────────────────────────

export interface StatusVisualConfig {
  color: string;
  bgColor: string;
  label: string;
}

export const STATUS_CONFIG: Record<DagNodeStatus, StatusVisualConfig> = {
  [DagNodeStatus.pending]: { color: 'gray', bgColor: '#e9ecef', label: 'Pending' },
  [DagNodeStatus.running]: { color: 'blue', bgColor: '#d0ebff', label: 'Running' },
  [DagNodeStatus.completed]: { color: 'green', bgColor: '#d3f9d8', label: 'Completed' },
  [DagNodeStatus.failed]: { color: 'red', bgColor: '#ffe3e3', label: 'Failed' },
  [DagNodeStatus.skipped]: { color: 'yellow', bgColor: '#fff9db', label: 'Skipped' },
  [DagNodeStatus.requiresHuman]: { color: 'teal', bgColor: '#c3fae8', label: 'Requires Human' },
  [DagNodeStatus.paused]: { color: 'orange', bgColor: '#fff4e6', label: 'Paused' },
};

// ── Palette categories ───────────────────────────────────────────────

export interface PaletteCategory {
  id: string;
  label: string;
  nodeTypes: DagNodeType[];
}

export const PALETTE_CATEGORIES: PaletteCategory[] = [
  {
    id: 'execution',
    label: 'Execution',
    nodeTypes: [DagNodeType.task, DagNodeType.human, DagNodeType.autonomous],
  },
  {
    id: 'logic',
    label: 'Logic',
    nodeTypes: [DagNodeType.decision, DagNodeType.condition],
  },
  {
    id: 'integration',
    label: 'Integration',
    nodeTypes: [DagNodeType.integration, DagNodeType.subWorkflow, DagNodeType.sagaActivation],
  },
  {
    id: 'safety',
    label: 'Safety',
    nodeTypes: [DagNodeType.validation, DagNodeType.policyEvaluation, DagNodeType.factSnapshot],
  },
  {
    id: 'actor',
    label: 'Actor',
    nodeTypes: [DagNodeType.actorDispatch],
  },
];
